import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { mediaBelow } from '../../../assets/styles/helper';
import themeColors from '../../../assets/styles/themeColors';
import { variables } from '../../../assets/styles/variables';
import Button from '../../Button/Button';
import AlgoliaLoader from '../../Loader/AlgoliaLoader';
import ModalBody from '../../Modal/ModalBody';
import SellFromHomeMetalChips from './SellFromHomeMetalChips';
import SellFromHomeProductList from './SellFromHomeProductList';
import SellFromHomeSearchInput from './SellFromHomeSearchInput';
import SellFromHomeWeightDropdown from './SellFromHomeWeightDropdown';

const FiltersRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

// Keep the metal chips mounted (only visually hidden) while the search field is
// expanded, so their MetalType refinement — and the other filters' state — is
// preserved and restored when the search field is closed.
const ChipsWrap = styled.div`
  ${({ $hidden }) => $hidden && 'display: none;'}
`;

const FieldLabel = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  color: ${themeColors.colorLabel};
  margin: 16px 0 6px;
`;

const Divider = styled.hr`
  border: 0;
  border-top: 1px solid ${themeColors.colorBorderPrimary};
  /* Negative side margins cancel ModalBody's 16px horizontal padding so the
     rule spans the full modal width. */
  margin: 20px -16px;
`;

const Heading = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  color: ${themeColors.colorTextSecondary};
  margin: 0 0 12px;
`;

const ListSection = styled.div`
  flex: 1;
  min-height: 0;
  max-height: 320px;
  display: flex;
  flex-direction: column;

  ${mediaBelow(variables.breakpoints.bpMd)} {
    max-height: none;
  }
`;

const FooterDivider = styled.hr`
  border: 0;
  border-top: 1px solid ${themeColors.colorBorderPrimary};
  margin: 0 -16px 16px;
`;

const Footer = styled.div`
  margin-top: auto;

  > button {
    width: 100%;
  }

  /* Figma disabled state: solid grey (colorGrey3 #ADB1C7), not the default
     translucent primary, and full opacity. :hover included to beat the primary
     variant's own :disabled:hover rule. */
  > button:disabled,
  > button:disabled:hover {
    background: ${themeColors.colorGrey3};
    border-color: ${themeColors.colorGrey3};
    opacity: 1;
  }
`;

const SellFromHomeProductSelection = ({
  selectedHit,
  setSelectedHit,
  handleNext,
}) => {
  const { t } = useTranslation();
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);

  return (
    <ModalBody>
      <FiltersRow>
        <ChipsWrap $hidden={isSearchExpanded}>
          <SellFromHomeMetalChips attribute="MetalType" operator="or" />
        </ChipsWrap>
        <SellFromHomeSearchInput
          isExpanded={isSearchExpanded}
          onToggleExpanded={() => setIsSearchExpanded((open) => !open)}
        />
      </FiltersRow>
      <FieldLabel>{t('sellWizard.sfhWizard.weightLabel')}</FieldLabel>
      <SellFromHomeWeightDropdown
        attribute="WeightCaption"
        operator="or"
        limit={50}
      />
      <Divider />
      <Heading>{t('sellWizard.sfhWizard.productListHeading')}</Heading>
      <ListSection>
        <AlgoliaLoader>
          <SellFromHomeProductList
            selectedCode={selectedHit?.Code}
            onSelect={(hit) => setSelectedHit(hit)}
          />
        </AlgoliaLoader>
      </ListSection>
      <Footer>
        <FooterDivider />
        <Button
          variant="primary"
          textTransform="none"
          disabled={!selectedHit}
          onClick={handleNext}
        >
          {t('sellWizard.sfhWizard.continue')}
        </Button>
      </Footer>
    </ModalBody>
  );
};

SellFromHomeProductSelection.propTypes = {
  selectedHit: PropTypes.shape({
    Code: PropTypes.string,
    Caption: PropTypes.string,
    WeightCaption: PropTypes.string,
  }),
  setSelectedHit: PropTypes.func.isRequired,
  handleNext: PropTypes.func.isRequired,
};

export default SellFromHomeProductSelection;
